import React, { useState } from 'react';
import useReveal from '../hooks/use-reveal';
import { track } from '../lib/analytics';

const PILLARS = [
  {
    id: 'awareness',
    num: '01',
    title: 'Days 1–5 · Awareness',
    sub: 'I can see it',
    body: [
      <>Most of us are so caught up in our thoughts and feelings that we never actually <strong>look at them</strong>. We react, we spiral, we go on autopilot — without ever stepping back to notice what's happening.</>,
      <>The first five days are about developing that observer. Not judging. Not fixing. Just seeing. Your thoughts, your feelings, your triggers, the patterns you run without choosing to.</>,
    ],
    outcome:
      '"By day five, most people say something they hadn\'t expected: I didn\'t realise I was doing that."',
  },
  {
    id: 'responsibility',
    num: '02',
    title: 'Days 6–10 · Responsibility',
    sub: 'This is mine to work with',
    body: [
      <>This isn't about blame — not of yourself or anyone else. It's about something harder and more freeing than that: <strong>recognising what is yours to work with.</strong></>,
      <>The things that happened to you were real. But your reactions, your patterns, your choices from here — those belong to you. This pillar is where you start to separate what you inherited from what you're choosing to carry.</>,
    ],
    outcome:
      '"This is the pillar where people start to feel lighter. Not because anything changed — because they stopped waiting for someone else to change it."',
  },
  {
    id: 'presence',
    num: '03',
    title: 'Days 11–15 · Presence',
    sub: 'I can stay with it',
    body: [
      <>Most of us have spent years getting very good at not being present. Scrolling, planning, replaying, escaping. Anything to avoid just <strong>being here with what is</strong>.</>,
      <>Presence isn't meditation. It's the ability to stay in a moment — even an uncomfortable one — without needing to immediately fix it or flee it. These five days are about building that muscle, slowly.</>,
    ],
    outcome:
      '"People often describe this pillar as uncomfortable at first, then surprisingly quiet. Like the noise turns down slightly."',
  },
  {
    id: 'acceptance',
    num: '04',
    title: 'Days 16–20 · Acceptance',
    sub: "I don't need this to be different first",
    body: [
      <>Acceptance is the most misunderstood word in personal development. It doesn't mean giving up or being okay with everything. It means <strong>stopping the fight with reality long enough to actually move through it.</strong></>,
      <>A lot of people are waiting to feel better before they start living differently. This pillar gently questions that. What if you didn't have to wait?</>,
    ],
    outcome:
      '"This is where the real shift tends to happen. Not dramatic. Just a quiet loosening of something that\'s been held tight for a long time."',
  },
  {
    id: 'connection',
    num: '05',
    title: 'Days 21–25 · Connection',
    sub: 'I can show up as I actually am',
    body: [
      <>Most of the connections in our lives are built between masks. We show people a version of ourselves we think they can handle, and they do the same back. It feels like connection but often leaves us <strong>more alone than before.</strong></>,
      <>These five days are about practicing a different way of showing up. Not oversharing. Not performing. Just being a little more honest about what's actually there.</>,
    ],
    outcome:
      '"People are often surprised by how others respond when they drop the performance. The connection that comes back is usually more real than anything they\'d had before."',
  },
  {
    id: 'integration',
    num: '06',
    title: 'Days 26–30 · Integration',
    sub: 'This is how I live now',
    body: [
      <>The last five days aren't about learning something new. They're about <strong>making what you've found in the previous 25 days part of how you actually live.</strong></>,
      <>Identity. Habits. Trust in yourself. The loop that keeps you honest going forward. This isn't the end of something — it's the beginning of a different way of operating. One that starts from the inside.</>,
    ],
    outcome:
      '"Day 30 isn\'t a finish line. It\'s the first day of not needing the process anymore — because it\'s just become how you see."',
  },
];

const Pillars = () => {
  const head = useReveal();
  const list = useReveal();
  const [openId, setOpenId] = useState(PILLARS[0].id);

  return (
    <section
      data-testid="pillars-section"
      style={{ padding: '100px 24px', maxWidth: '900px', margin: '0 auto' }}
    >
      <div ref={head.ref} className={`reveal ${head.visible ? 'visible' : ''}`}>
        <div className="yup-eyebrow" style={{ marginBottom: '20px' }}>
          The process
        </div>
        <h2 className="yup-section-title" style={{ marginBottom: '24px' }}>
          Six pillars.<br />
          30 days.<br />
          <em>One layer at a time.</em>
        </h2>
      </div>

      <div
        ref={list.ref}
        className={`reveal ${list.visible ? 'visible' : ''}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1px',
          marginTop: '48px',
          background: 'var(--yup-border)',
        }}
      >
        {PILLARS.map((p) => {
          const isOpen = openId === p.id;
          return (
            <div
              key={p.id}
              data-testid={`pillar-${p.id}`}
              style={{ background: 'var(--yup-bg)', userSelect: 'none' }}
            >
              <button
                type="button"
                onClick={() => {
                  const nextOpen = isOpen ? null : p.id;
                  setOpenId(nextOpen);
                  if (nextOpen) track('pillar_open', { value: p.id });
                }}
                aria-expanded={isOpen}
                data-testid={`pillar-toggle-${p.id}`}
                className="w-full"
                style={{
                  padding: '24px 24px',
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                  <span
                    className="font-serif"
                    style={{
                      fontSize: '32px',
                      fontWeight: 300,
                      color: isOpen ? 'var(--yup-accent)' : 'var(--yup-border)',
                      lineHeight: 1,
                      flexShrink: 0,
                      width: '40px',
                      transition: 'color 0.2s',
                    }}
                  >
                    {p.num}
                  </span>
                  <span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: '1px',
                        textTransform: 'uppercase',
                        color: 'var(--yup-accent)',
                        marginBottom: '4px',
                      }}
                    >
                      {p.title}
                    </span>
                    <span
                      className="font-serif"
                      style={{
                        fontSize: '18px',
                        fontStyle: 'italic',
                        color: 'var(--yup-muted)',
                        lineHeight: 1.3,
                      }}
                    >
                      {p.sub}
                    </span>
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  style={{
                    fontSize: '18px',
                    color: isOpen ? 'var(--yup-accent)' : 'var(--yup-muted)',
                    transition: 'transform 0.3s ease, color 0.2s',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    flexShrink: 0,
                  }}
                >
                  ↓
                </span>
              </button>

              <div
                style={{
                  maxHeight: isOpen ? '600px' : '0px',
                  overflow: 'hidden',
                  transition: 'max-height 0.4s ease',
                }}
              >
                <div
                  style={{
                    padding: '0 24px 28px 84px',
                    borderTop: '1px solid var(--yup-border)',
                  }}
                >
                  {p.body.map((para, i) => (
                    <p
                      key={i}
                      style={{
                        fontSize: '15px',
                        color: 'var(--yup-muted)',
                        lineHeight: 1.8,
                        marginTop: '20px',
                      }}
                    >
                      {para}
                    </p>
                  ))}
                  <div
                    className="font-serif"
                    style={{
                      marginTop: '16px',
                      padding: '14px 18px',
                      background: 'var(--yup-surface)',
                      borderLeft: '2px solid var(--yup-accent)',
                      fontSize: '16px',
                      fontStyle: 'italic',
                      color: 'var(--yup-ink)',
                      lineHeight: 1.5,
                    }}
                  >
                    {p.outcome}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Pillars;
